import { memo, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    TableContainer,
    Paper,
    CircularProgress,
    Box,
} from '@mui/material';
import { type SortColumn, type SubmissionsTableProps } from '../api';
import { SubmissionsFiltersBar } from './SubmissionsFiltersBar';

// oxlint-disable-next-line max-lines-per-function
function SubmissionsTableComponent({
    submissions,
    totalCount,
    page,
    perPage,
    sort,
    direction,
    filters,
    isLoading,
    columns,
    showFilters = true,
    showPagination = true,
    visibleFilters,
    disablePortal,
    getRowSx,
    onPageChange,
    onPerPageChange,
    onSortChange,
    onFiltersChange,
}: SubmissionsTableProps) {
    const handleSortClick = useCallback(
        (column: SortColumn) => {
            const isCurrentSort = sort === column;
            const newDirection = isCurrentSort && direction === 'asc' ? 'desc' : 'asc';
            onSortChange(column, newDirection);
        },
        [sort, direction, onSortChange],
    );

    const handlePageChange = useCallback(
        (_event: unknown, newPage: number) => {
            onPageChange(newPage + 1); // MUI uses 0-indexed pages
        },
        [onPageChange],
    );

    const handleRowsPerPageChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            // Only call onPerPageChange - it already handles resetting page to 1.
            // Calling both would cause a stale closure race condition where
            // onPageChange uses the OLD perPage value from its closure.
            onPerPageChange(parseInt(event.target.value, 10));
        },
        [onPerPageChange],
    );

    return (
        <div className="submissions-table">
            {showFilters && (
                <SubmissionsFiltersBar
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    visibleFilters={visibleFilters}
                    disablePortal={disablePortal}
                />
            )}

            <Paper sx={{ position: 'relative' }}>
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map(column => (
                                    <TableCell
                                        key={column.id}
                                        sx={{
                                            width: column.width,
                                            fontWeight: 'bold',
                                            backgroundColor: 'grey.100',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {column.sortable && column.sortKey ? (
                                            <TableSortLabel
                                                active={sort === column.sortKey}
                                                direction={sort === column.sortKey ? direction : 'asc'}
                                                onClick={() => handleSortClick(column.sortKey!)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {submissions.map(submission => (
                                <TableRow
                                    key={submission.id}
                                    hover
                                    sx={[
                                        { '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } },
                                        ...(getRowSx ? [getRowSx(submission)].flat() : []),
                                    ]}
                                >
                                    {columns.map(column => (
                                        <TableCell key={column.id}>{column.render(submission)}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {submissions.length === 0 && !isLoading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        No submissions found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {showPagination && (
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page - 1} // MUI uses 0-indexed pages
                        rowsPerPage={perPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        slotProps={{ select: disablePortal ? { native: true } : undefined }}
                    />
                )}
            </Paper>
        </div>
    );
}

export const SubmissionsTable = memo(SubmissionsTableComponent);
